import type {
  ActionResult,
  ExportFileListItem,
  OperationListItem,
  OrderBulkAction,
  OrderBulkActionResult,
  OrderCsvPreview,
  OrderDetail,
  OrderImportBody,
  OrderImportResult,
  Paginated,
  UnifiedOrderListItem,
  UnifiedOrderListQuery,
} from '@flux/shared';
import { apiFetch } from '@/lib/api/client.js';

/** `/api/orders` — écran Commandes (`02` §3, §4). Fonctions pures, réutilisées par les hooks de ce dossier. */

export function fetchUnifiedOrders(
  query: UnifiedOrderListQuery,
): Promise<Paginated<UnifiedOrderListItem>> {
  return apiFetch<Paginated<UnifiedOrderListItem>>('/api/orders/unified', { query });
}

export function fetchOrderDetail(id: string): Promise<OrderDetail> {
  return apiFetch<OrderDetail>(`/api/orders/${id}`);
}

export function fetchOrderCsvPreview(id: string, fileId: string | undefined): Promise<OrderCsvPreview> {
  return apiFetch<OrderCsvPreview>(`/api/orders/${id}/csv`, fileId ? { query: { fileId } } : {});
}

export function fetchOrderFiles(id: string): Promise<ExportFileListItem[]> {
  return apiFetch<ExportFileListItem[]>(`/api/orders/${id}/files`);
}

/** Options du filtre « opération commerciale » (`02` §3) — liste complète, pas de pagination utile ici. */
export function fetchOperationsOptions(): Promise<Paginated<OperationListItem>> {
  return apiFetch<Paginated<OperationListItem>>('/api/operations', { query: { page: 1, pageSize: 200 } });
}

export function sendOrderNow(id: string): Promise<ActionResult> {
  return apiFetch<ActionResult>(`/api/orders/${id}/send-now`, { method: 'POST' });
}

/** « Mettre à jour depuis Shopify » : relecture et aperçu recalculé, rien n'est envoyé. */
export function refreshOrder(id: string): Promise<ActionResult> {
  return apiFetch<ActionResult>(`/api/orders/${id}/refresh`, { method: 'POST' });
}

/** « Renvoyer la commande à Martelet » : relecture Shopify, nouveau fichier, envoi. */
export function regenerateOrder(id: string): Promise<ActionResult> {
  return apiFetch<ActionResult>(`/api/orders/${id}/regenerate`, { method: 'POST' });
}

export function ignoreOrder(id: string, reason: string): Promise<ActionResult> {
  return apiFetch<ActionResult>(`/api/orders/${id}/ignore`, { method: 'POST', body: { reason } });
}

export function reactivateOrder(id: string): Promise<ActionResult> {
  return apiFetch<ActionResult>(`/api/orders/${id}/reactivate`, { method: 'POST' });
}

export function markOrderHandled(id: string): Promise<ActionResult> {
  return apiFetch<ActionResult>(`/api/orders/${id}/mark-handled`, { method: 'POST' });
}

export function importOrder(body: OrderImportBody): Promise<OrderImportResult> {
  return apiFetch<OrderImportResult>('/api/orders/import', { method: 'POST', body });
}

export function bulkOrderAction(
  kind: OrderBulkAction,
  orderIds: readonly string[],
  reason: string | undefined,
): Promise<OrderBulkActionResult> {
  return apiFetch<OrderBulkActionResult>(`/api/orders/bulk/${kind}`, {
    method: 'POST',
    body: { orderIds, ...(reason === undefined ? {} : { reason }) },
  });
}
